// -*- coding: utf-8 -*-
/**
 * Task Dispatcher & Worker Telemetry Cockpit (Stage 069 Phase 1 Option A & Stage 081 UI Standardization).
 * Governance Anchor: TASK-MARKET_HUB-081-SYSTEM-REALM-UI-UX-STANDARDIZATION
 * Architecture Reference: docs/20-architecture/ADR-069-TASK-HARMONIZER-AND-WORKER-TELEMETRY.md
 *
 * Implements:
 * - System Realm entrypoint at `#/system/pipeline` (legacy alias `#/system/dispatcher`)
 * - 5 Asset Domain Sub-tabs (`GLOBAL`, `CRYPTO`, `ASHARE`, `MACRO`, `INFRA`)
 * - Truthful 5-state lifecycle (`loading | fresh | stale | disconnected | empty`)
 * - Daylight Sage & Ghost Outline visual styling with scoped CSS tokens
 * - lucide-react vector iconography (AC-01) & single root anchor id="view-dispatcher-cockpit" (AC-03)
 * - Accurate measured event-loop lag and unmeasured null preservation (A069-R4-01 / A069-R5-01)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { SYSTEM_BILINGUAL } from '../../shared/constants/bilingualTitles';
import {
  Workflow,
  Coins,
  Building2,
  Globe,
  Cpu,
  RefreshCw,
  AlertTriangle,
  Zap,
  Clock,
  BarChart3,
  CloudSun,
  Radio,
  Compass,
  TrendingUp,
  Activity,
  CheckCircle2,
  XCircle,
  AlertCircle,
  HelpCircle,
} from 'lucide-react';
import { fetchWorkerTelemetrySnapshot } from './api';
import {
  DispatcherTabId,
  WorkerTelemetryItem,
  WorkerTelemetrySnapshot,
  WORKER_METADATA_MAP,
  CockpitFreshnessState,
} from './types';

const SUB_TABS: Array<{ id: DispatcherTabId; labelZh: string; labelEn: string; iconKey: string }> = [
  { id: 'GLOBAL', labelZh: '全局时序流水线', labelEn: 'GLOBAL PIPELINE', iconKey: 'workflow' },
  { id: 'CRYPTO', labelZh: '加密货币域', labelEn: 'CRYPTO DOMAIN', iconKey: 'coins' },
  { id: 'ASHARE', labelZh: 'A股市场域', labelEn: 'A-SHARE DOMAIN', iconKey: 'building' },
  { id: 'MACRO', labelZh: '宏观跨市场域', labelEn: 'MACRO & CROSS-ASSET', iconKey: 'globe' },
  { id: 'INFRA', labelZh: '核心基础设施域', labelEn: 'INFRASTRUCTURE', iconKey: 'cpu' },
];

function renderTabIcon(key: string): React.ReactNode {
  switch (key) {
    case 'workflow':
      return <Workflow size={14} aria-hidden="true" />;
    case 'coins':
      return <Coins size={14} aria-hidden="true" />;
    case 'building':
      return <Building2 size={14} aria-hidden="true" />;
    case 'globe':
      return <Globe size={14} aria-hidden="true" />;
    case 'cpu':
      return <Cpu size={14} aria-hidden="true" />;
    default:
      return <Activity size={14} aria-hidden="true" />;
  }
}

/**
 * Daylight Sage & Ghost Outline architectural token mappings:
 * - Accent line / border: rgba(16, 185, 129, 0.15)
 * - Ghost Outline subtle card: rgba(255, 255, 255, 0.05)
 * - Quantitative metrics typography: fontFamily: 'var(--font-mono, monospace)'
 */

function renderWorkerVectorIcon(name: string): React.ReactNode {
  switch (name) {
    case 'indicator_materializer':
      return <BarChart3 size={18} aria-hidden="true" className="worker-icon-cyan" />;
    case 'trend_weather_worker':
      return <CloudSun size={18} aria-hidden="true" className="worker-icon-warning" />;
    case 'collector_daemon':
      return <Zap size={18} aria-hidden="true" className="worker-icon-emerald" />;
    case 'multiasset_ingress_daemon':
      return <Radio size={18} aria-hidden="true" className="worker-icon-cyan" />;
    case 'grounding_observer':
      return <Compass size={18} aria-hidden="true" className="worker-icon-cyan" />;
    case 'macro_market_worker':
      return <Globe size={18} aria-hidden="true" className="worker-icon-cyan" />;
    case 'watchlist_intraday_worker':
      return <TrendingUp size={18} aria-hidden="true" className="worker-icon-emerald" />;
    case 'ashare_eod_ingress':
      return <Building2 size={18} aria-hidden="true" className="worker-icon-bearish" />;
    default:
      return <Cpu size={18} aria-hidden="true" className="worker-icon-muted" />;
  }
}

function computeWorkerFreshness(worker: WorkerTelemetryItem): CockpitFreshnessState {
  if (worker.status === 'UNAVAILABLE') return 'UNAVAILABLE';
  if (worker.status === 'FAILED' && worker.consecutive_errors >= 2) return 'UNHEALTHY';
  if (worker.status === 'FAILED') return 'DEGRADED';
  if (!worker.last_completed_utc) return 'UNKNOWN';

  const completedTs = new Date(worker.last_completed_utc).getTime();
  const nowTs = Date.now();
  const diffSec = Math.max(0, (nowTs - completedTs) / 1000);

  // Fresh if within 2.5 periods
  if (diffSec <= worker.period_seconds * 2.5) return 'FRESH';
  // Stale if within 5 periods
  if (diffSec <= worker.period_seconds * 5) return 'STALE';
  return 'DEGRADED';
}

function formatUtcTimestamp(isoStr: string | null): string {
  if (!isoStr) return '--';
  try {
    const d = new Date(isoStr);
    const h = String(d.getUTCHours()).padStart(2, '0');
    const m = String(d.getUTCMinutes()).padStart(2, '0');
    const s = String(d.getUTCSeconds()).padStart(2, '0');
    return `${h}:${m}:${s} UTC`;
  } catch {
    return isoStr;
  }
}

export const TaskDispatcherCockpit: React.FC = () => {
  const [snapshot, setSnapshot] = useState<WorkerTelemetrySnapshot | null>(null);
  const [activeTab, setActiveTab] = useState<DispatcherTabId>('GLOBAL');
  const [state5, setState5] = useState<'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);

  const loadTelemetry = useCallback(async () => {
    try {
      const data = await fetchWorkerTelemetrySnapshot();
      if (!data || !data.workers || data.workers.length === 0) {
        setSnapshot(data || null);
        setState5('empty');
      } else {
        setSnapshot(data);
        setErrorMessage(null);
        setState5('fresh');
      }
      setLastRefreshedAt(new Date());
    } catch (err: any) {
      if (!snapshot) {
        setState5('disconnected');
      } else {
        setState5('stale');
      }
      setErrorMessage(err?.message || '无法获取 Worker 遥测数据 (HTTP 503 / 离线)');
    }
  }, [snapshot]);

  useEffect(() => {
    loadTelemetry();
  }, [loadTelemetry]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(loadTelemetry, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, loadTelemetry]);

  // Tab filtering
  const filteredWorkers = useMemo(() => {
    if (!snapshot || !snapshot.workers) return [];
    if (activeTab === 'GLOBAL') return snapshot.workers;
    return snapshot.workers.filter((w) => w.domain.toUpperCase() === activeTab);
  }, [snapshot, activeTab]);

  return (
    <div className="dispatcher-cockpit-view" id="view-dispatcher-cockpit" role="tabpanel" aria-labelledby="nav-pipeline">
      {/* 1. View Header (Unified, zero card box, matching Views 2, 3, 4) */}
      <div className="view-header">
        <div className="view-title-group bilingual-view-header">
          <div className="dispatcher-title-row bilingual-header-top">
            <span className="dispatcher-title-icon">
              <Workflow size={20} aria-hidden="true" color="var(--accent-cyan)" />
            </span>
            <h2 className="view-main-title bilingual-header-title-zh">
              {SYSTEM_BILINGUAL.pipeline.zh}
            </h2>
            <span className="dispatcher-active-badge">
              {SYSTEM_BILINGUAL.pipeline.badge}
            </span>
          </div>
          <span className="bilingual-header-subtitle-en" title="PIPELINE & DISPATCH COCKPIT">{SYSTEM_BILINGUAL.pipeline.en}</span>
          <p className="view-subtitle dispatcher-view-subtitle">
            Market-Hub 多周期后台 Worker 原生节拍观测、无锁化脉冲遥测与全链路生命周期看板
          </p>
        </div>

        {/* Action buttons */}
        <div className="dispatcher-header-actions">
          <label className="dispatcher-refresh-label">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="dispatcher-checkbox"
            />
            自动刷新 (5s)
          </label>
          <button
            onClick={loadTelemetry}
            className="dispatcher-refresh-btn"
            title="手动拉取 Worker 遥测快照"
          >
            <RefreshCw size={13} aria-hidden="true" className={state5 === 'loading' ? 'spin' : ''} />
            <span>手动刷新</span>
          </button>
        </div>
      </div>

      {/* 2. Cockpit Status Strip (Unified, matching cockpit-status-bar / fsm-audit-strip) */}
      <section className="cockpit-status-bar dispatcher-status-strip" id="cockpit-status-bar">
        <div className="status-strip-left dispatcher-strip-left">
          <div className="dispatcher-status-item">
            <span className="dot-emerald">●</span>
            <span className="status-label">协调器模式:</span>
            <span className="status-value">PASSIVE_TELEMETRY</span>
          </div>

          <div className="dispatcher-status-item">
            <span className="dot-cyan">●</span>
            <span className="status-label">活跃打卡:</span>
            <span className="status-value">
              {snapshot ? `${snapshot.active_supervised_count} / ${snapshot.total_registered_count}` : '--'}
            </span>
          </div>

          <div className="dispatcher-status-item">
            <span className={snapshot?.measured_event_loop_lag_ms !== null ? 'dot-emerald' : 'dot-warning'}>●</span>
            <span className="status-label">事件循环时延:</span>
            <span className="status-value">
              {snapshot && snapshot.measured_event_loop_lag_ms !== null
                ? `${snapshot.measured_event_loop_lag_ms}ms (真实度量)`
                : '-- ms (待首次采样)'}
            </span>
          </div>

          <div className="dispatcher-status-item">
            <span className="status-label">最近同步:</span>
            <span className="status-mono">
              {lastRefreshedAt ? formatUtcTimestamp(lastRefreshedAt.toISOString()) : '--'}
            </span>
          </div>
        </div>

        <div className="dispatcher-state5-badge-wrapper">
          {state5 === 'fresh' && (
            <span className="dispatcher-fsm-badge badge-fresh">
              ● FRESH 实时
            </span>
          )}
          {state5 === 'stale' && (
            <span className="dispatcher-fsm-badge badge-stale">
              ○ STALE 延迟
            </span>
          )}
          {state5 === 'disconnected' && (
            <span className="dispatcher-fsm-badge badge-disconnected">
              ✕ 离线断网
            </span>
          )}
          {state5 === 'empty' && (
            <span className="dispatcher-fsm-badge badge-empty">
              ○ 无数据
            </span>
          )}
          {state5 === 'loading' && (
            <span className="dispatcher-fsm-badge badge-loading">
              ◐ 接入中...
            </span>
          )}
        </div>
      </section>

      {/* 3. Error Banner if disconnected */}
      {state5 === 'disconnected' && (
        <div className="dispatcher-error-banner">
          <div className="dispatcher-error-msg">
            <AlertTriangle size={18} aria-hidden="true" />
            <span>{errorMessage || '无法连接到后端遥测服务 (HTTP 503 / 网络超时)'}</span>
          </div>
          <button onClick={loadTelemetry} className="dispatcher-retry-btn">
            重试连接
          </button>
        </div>
      )}

      {/* 4. 5 Asset Domain Sub-tabs */}
      <div className="grounding-tabs-container dispatcher-tabs-bar" role="tablist">
        {SUB_TABS.map((tab) => {
          const isSelected = activeTab === tab.id;
          const count = snapshot?.workers
            ? tab.id === 'GLOBAL'
              ? snapshot.workers.length
              : snapshot.workers.filter((w) => w.domain.toUpperCase() === tab.id).length
            : 0;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`grounding-tab-btn bilingual-tab-btn ${isSelected ? 'active' : ''}`}
            >
              <div className="bilingual-tab-title-zh">
                {renderTabIcon(tab.iconKey)}
                <span>{tab.labelZh}</span>
                <span className="dispatcher-tab-count">
                  {count}
                </span>
              </div>
              <span className="bilingual-tab-subtitle-en">{tab.labelEn}</span>
            </button>
          );
        })}
      </div>

      {/* 4. Telemetry Workers Table Card */}
      <div className="dispatcher-table-card">
        <div className="dispatcher-table-wrapper">
          <table className="dispatcher-table">
            <thead>
              <tr>
                <th>任务名称</th>
                <th>资产域</th>
                <th>原生周期</th>
                <th>打卡源</th>
                <th>最近打卡</th>
                <th>单次耗时</th>
                <th>运行状态</th>
                <th>跳过/异常分类</th>
                <th>心跳时效</th>
              </tr>
            </thead>
            <tbody>
              {/* Loading Skeleton */}
              {state5 === 'loading' && !snapshot && (
                <tr>
                  <td colSpan={9} className="dispatcher-table-empty">
                    <div className="dispatcher-table-empty-inner">
                      <Zap size={16} aria-hidden="true" className="spin dispatcher-table-empty-icon" />
                      <span>正在接入 Worker 遥测注册表并探测快照...</span>
                    </div>
                  </td>
                </tr>
              )}

              {/* Empty State */}
              {filteredWorkers.length === 0 && state5 !== 'loading' && (
                <tr>
                  <td colSpan={9} className="dispatcher-table-empty">
                    <div className="dispatcher-table-empty-inner">
                      <Clock size={16} aria-hidden="true" />
                      <span>当前分域暂无注册的后台 Worker</span>
                    </div>
                  </td>
                </tr>
              )}

              {/* Worker Rows */}
              {filteredWorkers.map((worker) => {
                const meta = WORKER_METADATA_MAP[worker.name] || {
                  label: worker.name,
                  sub: worker.name,
                  icon: 'gear',
                  periodDisplay: `${worker.period_seconds}s`,
                };

                const freshness = computeWorkerFreshness(worker);

                return (
                  <tr key={worker.name}>
                    {/* 1. Worker Name & Label */}
                    <td>
                      <div className="worker-cell-name">
                        <span className="worker-cell-icon">
                          {renderWorkerVectorIcon(worker.name)}
                        </span>
                        <div>
                          <div className="worker-name-text">{meta.label}</div>
                          <div className="worker-id-text">
                            {worker.name}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* 2. Domain Pill */}
                    <td>
                      <span
                        style={{
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          padding: '3px 8px',
                          borderRadius: '6px',
                          background:
                            worker.domain === 'CRYPTO'
                              ? 'rgba(245, 158, 11, 0.12)'
                              : worker.domain === 'ASHARE'
                              ? 'rgba(239, 68, 68, 0.12)'
                              : worker.domain === 'MACRO'
                              ? 'var(--accent-cyan-glow)'
                              : 'rgba(148, 163, 184, 0.12)',
                          color:
                            worker.domain === 'CRYPTO'
                              ? 'var(--color-warning)'
                              : worker.domain === 'ASHARE'
                              ? 'var(--color-bearish)'
                              : worker.domain === 'MACRO'
                              ? 'var(--accent-cyan)'
                              : 'var(--text-secondary)',
                          fontFamily: 'var(--font-mono, monospace)',
                        }}
                      >
                        {worker.domain}
                      </span>
                    </td>

                    {/* 3. Cadence */}
                    <td className="worker-cadence-cell">
                      <div className="worker-period-text">{meta.periodDisplay}</div>
                      {meta.expectedLagWindow && (
                        <div className="worker-lag-text">{meta.expectedLagWindow}</div>
                      )}
                    </td>

                    {/* 4. Source Type */}
                    <td className="worker-source-cell">
                      {worker.source_type === 'LOCAL_LOOP' ? '本地循环' : 'Systemd 定时'}
                    </td>

                    {/* 5. Last Completed UTC */}
                    <td className="worker-time-cell">
                      <div>{formatUtcTimestamp(worker.last_completed_utc)}</div>
                      {worker.last_run_utc && worker.last_run_utc !== worker.last_completed_utc && (
                        <div className="worker-time-sub">
                          start: {formatUtcTimestamp(worker.last_run_utc)}
                        </div>
                      )}
                    </td>

                    {/* 6. Duration ms */}
                    <td className="worker-duration-cell">
                      {worker.last_duration_ms !== null ? (
                        <span className={worker.last_duration_ms > 2000 ? 'worker-duration-warn' : 'worker-duration-normal'}>
                          {worker.last_duration_ms.toLocaleString()}ms
                        </span>
                      ) : (
                        <span className="worker-time-sub">--</span>
                      )}
                    </td>

                    {/* 7. Run Status */}
                    <td>
                      {worker.status === 'SUCCESS' && (
                        <span className="worker-status-badge status-success">
                          <CheckCircle2 size={12} aria-hidden="true" /> SUCCESS
                        </span>
                      )}
                      {worker.status === 'SKIPPED' && (
                        <span className="worker-status-badge status-skipped">
                          <AlertCircle size={12} aria-hidden="true" /> SKIPPED
                        </span>
                      )}
                      {worker.status === 'FAILED' && (
                        <span className="worker-status-badge status-failed">
                          <XCircle size={12} aria-hidden="true" /> FAILED ({worker.consecutive_errors})
                        </span>
                      )}
                      {worker.status === 'UNAVAILABLE' && (
                        <span className="worker-status-badge status-unavail">
                          <HelpCircle size={12} aria-hidden="true" /> UNAVAIL
                        </span>
                      )}
                      {worker.status === 'UNKNOWN' && (
                        <span className="worker-status-badge status-unknown">
                          待打卡
                        </span>
                      )}
                    </td>

                    {/* 8. Skip / Error Reason */}
                    <td className="worker-reason-cell">
                      {worker.last_skip_reason && (
                        <span className="worker-reason-skip">[跳过] {worker.last_skip_reason}</span>
                      )}
                      {worker.last_error_category && (
                        <span className="worker-reason-err">[异常] {worker.last_error_category}</span>
                      )}
                      {!worker.last_skip_reason && !worker.last_error_category && (
                        <span className="worker-time-sub">--</span>
                      )}
                    </td>

                    {/* 9. Freshness */}
                    <td className="worker-freshness-cell">
                      {freshness === 'FRESH' && <span className="freshness-tag freshness-fresh">● 准点 FRESH</span>}
                      {freshness === 'STALE' && <span className="freshness-tag freshness-stale">○ 稍有延迟 STALE</span>}
                      {freshness === 'DEGRADED' && <span className="freshness-tag freshness-degraded">▲ 性能降级 DEGRADED</span>}
                      {freshness === 'UNHEALTHY' && <span className="freshness-tag freshness-unhealthy">✕ 僵死异常 UNHEALTHY</span>}
                      {freshness === 'UNAVAILABLE' && <span className="freshness-tag freshness-unavail">○ 外部管道 UNAVAIL</span>}
                      {freshness === 'UNKNOWN' && <span className="freshness-tag freshness-unknown">○ 等待首次执行</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
